import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function _readSessionIdFromHash() {
  if (typeof window === 'undefined') return null;
  const h = window.location.hash || '';
  if (!h.includes('session_id=')) return null;
  const m = h.match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function _clearAuthHash() {
  if (typeof window === 'undefined') return;
  try {
    const { pathname, search } = window.location;
    window.history.replaceState({}, document.title, pathname + search);
  } catch (_) {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    // 1) If returning from Google OAuth, exchange session_id → JWT first.
    const sid = _readSessionIdFromHash();
    if (sid) {
      try {
        const { data } = await api.post('/auth/google/session', { session_id: sid });
        localStorage.setItem('ep_token', data.access_token);
        setUser(data.user);
        _clearAuthHash();
        setLoading(false);
        return;
      } catch (err) {
        // Fall through to normal /auth/me check; surface the error only in the browser console.
        console.warn('Google session exchange failed', err);
        _clearAuthHash();
      }
    }

    const token = localStorage.getItem('ep_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('ep_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('ep_token', data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('ep_token', data.access_token);
    setUser(data.user);
    return data.user;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href =
      'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
  };

  const logout = () => {
    localStorage.removeItem('ep_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
